import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useRef, useState, useCallback, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import "../Footer/index.js";
import "../Header/index.js";
import "../Section/index.js";
import "../SectionHeader/index.js";
import { withThemeAndSize } from "../../withThemeAndSize/index.js";
import { DEFAULT_PORTAL_ID } from "../../../constants/config.js";
import { cn as clsx } from "../../../helpers/react.js";
import { isFunction } from "../../../helpers/utils.js";
import '../../../assets/index53.css';const root = "_root_nhkax_1";
const hiddenParent = "_hiddenParent_nhkax_14";
const backdrop = "_backdrop_nhkax_20";
const transparent = "_transparent_nhkax_37";
const container = "_container_nhkax_41";
const s = {
  root,
  hiddenParent,
  backdrop,
  transparent,
  container
};
const Backdrop = ({ invisible, hidden, transparent: transparent2, className, ...props }) => {
  if (invisible || hidden) {
    return null;
  }
  return /* @__PURE__ */ jsx(
    "div",
    {
      ...props,
      className: clsx(className, s.backdrop, {
        [s.transparent]: transparent2
      })
    }
  );
};
const DATA_MODAL_ATTR = "data-component-modal";
const Modal = withThemeAndSize(
  forwardRef(
    ({
      isOpen,
      onOpenStateChange,
      ignoreCloseOnBackdropClick,
      ignoreCloseOnEscapeKey,
      portalQuery = DEFAULT_PORTAL_ID,
      theme,
      size,
      children,
      backdropProps = {},
      containerProps = {},
      parentModalRef,
      copyParentHeight,
      focusOnOpen = true,
      ...rest
    }, ref) => {
      const containerRef = useRef(null);
      const [internalContainerStyle, setInternalContainerStyle] = useState({});
      const handleOpenChange = useCallback(
        (nextIsOpen, event) => {
          if (isFunction(onOpenStateChange) && isOpen !== nextIsOpen) {
            onOpenStateChange(nextIsOpen, event);
          }
        },
        [isOpen, onOpenStateChange]
      );
      useLayoutEffect(() => {
        var _a;
        const parentModal = parentModalRef == null ? void 0 : parentModalRef.current;
        if (parentModal) {
          if (copyParentHeight) {
            setInternalContainerStyle({
              height: (_a = parentModal.querySelector(`[${DATA_MODAL_ATTR}]`)) == null ? void 0 : _a.clientHeight
            });
          } else {
            setInternalContainerStyle({});
          }
          if (isOpen) {
            parentModal.classList.add(s.hiddenParent);
          } else {
            parentModal.classList.remove(s.hiddenParent);
          }
        }
      }, [isOpen]);
      useEffect(() => {
        const handleKeyDown = (event) => {
          if (!ignoreCloseOnEscapeKey && event.key === "Escape") {
            handleOpenChange(false, event);
          }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
          document.removeEventListener("keydown", handleKeyDown);
        };
      }, [handleOpenChange, ignoreCloseOnEscapeKey]);
      const handleBackdropClick = useCallback(
        (event) => {
          var _a;
          (_a = backdropProps == null ? void 0 : backdropProps.onClick) == null ? void 0 : _a.call(backdropProps, event);
          if (!ignoreCloseOnBackdropClick) {
            handleOpenChange(false, event);
          }
        },
        [ignoreCloseOnBackdropClick, backdropProps, handleOpenChange]
      );
      useEffect(() => {
        var _a;
        if (isOpen && focusOnOpen) {
          (_a = containerRef.current) == null ? void 0 : _a.focus();
        }
      }, [focusOnOpen, isOpen]);
      if (!isOpen) {
        return null;
      }
      const portalContainer = document.getElementById(portalQuery) || document.body;
      const containerStyle = {
        ...internalContainerStyle,
        ...containerProps.style
      };
      const containerPropsWithDataAttr = {
        ...containerProps,
        [DATA_MODAL_ATTR]: true
      };
      return createPortal(
        /* @__PURE__ */ jsxs("div", { ...rest, className: clsx(rest.className, s.root), ref, children: [
          /* @__PURE__ */ jsx(Backdrop, { ...backdropProps, onClick: handleBackdropClick }),
          /* @__PURE__ */ jsx(
            "div",
            {
              role: "dialog",
              "aria-modal": "true",
              tabIndex: 0,
              ...containerPropsWithDataAttr,
              style: containerStyle,
              className: clsx(containerProps.className, s.container),
              ref: containerRef,
              "data-modal-component": DATA_MODAL_ATTR,
              children
            }
          )
        ] }),
        portalContainer
      );
    }
  )
);
Modal.displayName = "Modal";
export {
  Modal
};
